// API client ID for authentication (replace with real value in production)
export const API_CLIENT_ID = "45522a1e-b8eb-4a28-8e46-c633eb2bfdc1";

// Helper to get the current Bearer token for API requests
// Adjust this function to match your auth storage (localStorage, sessionStorage, MSAL, etc.)

export const ADMIN_GROUP_ID = import.meta.env.VITE_ADMIN_GROUP_ID || "YOUR_ADMIN_GROUP_ID";

export const DEVELOPER_GROUP_ID = import.meta.env.VITE_DEVELOPER_GROUP_ID || "YOUR_DEVELOPER_GROUP_ID";

export const getAuthToken = () => {
  // Example: token stored in localStorage under 'access_token'
  return localStorage.getItem('access_token');
};
// Helper to get API base URL (add this function for service modules)
export const getApiBaseUrl = () => {
  // Prefer VITE_API_BASE_URL if set, otherwise fallback to localhost for dev, or production API for prod
  if (import.meta.env.VITE_API_BASE_URL) return import.meta.env.VITE_API_BASE_URL;
  if (isDevelopment) return 'http://localhost:7071/api';
  return 'https://api.esaienergy.com/api';
};
// Application configuration settings
// Environment detection
const isDevelopment = import.meta.env.DEV || process.env.NODE_ENV === 'development';
const isProduction = import.meta.env.PROD || process.env.NODE_ENV === 'production';

// Development configuration
const DEVELOPMENT_CONFIG = {
  version: 'alpha-4',
  buildNumber: import.meta.env.VITE_BUILD_NUMBER || 'dev',
  enableDebugComponents: false,
  // Data query settings for development
  data: {
    // Maximum number of rows to return across ALL pages/requests
    maxRows: 20000,
    
    // Number of records to request per GraphQL call (page size)
    pageSize: 5000,
    
    // Default limit for queries when maxRows is not specified
    defaultLimit: 2000
  },
  
  // Cache settings
  cache: {
    memoryItems: 10,  // Number of cache entries in memory
    maxItemSizeForMemory: 10000,  // Smaller memory cache for development
    maxItemSizeForLocalStorage: 2000  // Smaller localStorage cache for development
  },
  
  // API settings
  api: {
    timeout: 15000, // 15 seconds - shorter for development
    retryAttempts: 2,
    blobFileManagerUrl: `${import.meta.env.VITE_API_BASE_URL}/blobFileManager`
  },
  
  // Development settings
  development: {
    enableConsoleLogging: true,
    enableCacheLogging: true,
    enablePerformanceLogging: true,
    showQueryDetails: true
  }
};

// Production configuration
const PRODUCTION_CONFIG = {
  version: 'alpha-4',
  buildNumber: import.meta.env.VITE_BUILD_NUMBER || 'prod',
  enableDebugComponents: false,
  // Data query settings for production
  data: {
    // Maximum number of rows to return across ALL pages/requests
    maxRows: 60000,
    
    // Number of records to request per GraphQL call (page size)
    pageSize: 5000,
    
    // Default limit for queries when maxRows is not specified
    defaultLimit: 10000
  },
  
  // Cache settings
  cache: {
    memoryItems: 10,  // Number of cache entries in memory
    maxItemSizeForMemory: 500000,  // Larger memory cache for production
    maxItemSizeForLocalStorage: 10000  // Larger localStorage cache for production
  },
  
  // API settings
  api: {
    timeout: 30000, // 30 seconds - longer for production
    retryAttempts: 3,
    blobFileManagerUrl: `${import.meta.env.VITE_API_BASE_URL}/blobFileManager`
  },
  
  // Development settings
  development: {
    enableConsoleLogging: false,  // Disable verbose logging in production
    enableCacheLogging: false,
    enablePerformanceLogging: false,
    showQueryDetails: false
  }
};

// Select configuration based on environment
const CONFIG = isDevelopment ? DEVELOPMENT_CONFIG : PRODUCTION_CONFIG;

// Log current environment and config
if (isDevelopment) {
  console.log('🔧 Running in DEVELOPMENT mode');
  console.log('📊 Max rows limit:', CONFIG.data.maxRows);
} else {
  console.log('🚀 Running in PRODUCTION mode');
}

// Helper function to get current configuration
export const getCurrentConfig = () => {
  return CONFIG;
};

// Helper to get version
export const getVersion = () => CONFIG.version;

// Helper to get build number
export const getBuildNumber = () => CONFIG.buildNumber;

// Footer build label. Prefers the PR ID on PR-validation builds, otherwise
// pairs the auto-generated build number with a short commit SHA. Falls back
// to "dev" when nothing is injected (i.e. running `pnpm dev` locally).
//
// Defensive: when an Azure Pipelines `$(varName)` macro references an
// undefined variable, the literal string `$(varName)` is passed through to
// the build env. Treat any value of that shape as "not set" so we don't
// surface "PR-$(System.PullRequest.PullRequestId)" or similar in the UI.
const cleanPipelineValue = (raw) => {
  const trimmed = (raw || '').trim();
  if (!trimmed) return '';
  if (/^\$\(.+\)$/.test(trimmed)) return '';
  return trimmed;
};

export const getBuildLabel = () => {
  const prId = cleanPipelineValue(import.meta.env.VITE_BUILD_PR_ID);
  const buildNumber = cleanPipelineValue(import.meta.env.VITE_BUILD_NUMBER);
  const commitSha = cleanPipelineValue(import.meta.env.VITE_BUILD_COMMIT_SHA);

  if (prId) return `PR-${prId}`;
  if (buildNumber || commitSha) {
    const shortSha = commitSha ? commitSha.slice(0, 7) : 'unknown';
    return buildNumber ? `${buildNumber} (${shortSha})` : shortSha;
  }
  return 'dev';
};

// Helper function to get current environment
export const getEnvironment = () => {
  return isDevelopment ? 'development' : 'production';
};

// Helper function to check if development mode
export const isDev = () => {
  return isDevelopment;
};

// Helper function to check if production mode
export const isProd = () => {
  return isProduction;
};

// Helper function to get max rows setting
export const getMaxRows = () => {
  console.log('Getting max rows:', CONFIG.data.maxRows);
  return CONFIG.data.maxRows;
};

// Helper function to get page size setting
export const getPageSize = () => {
  return CONFIG.data.pageSize || CONFIG.data.defaultLimit;
};

// Helper function to check if max rows limit is enabled
export const isMaxRowsEnabled = () => {
  return CONFIG.data.maxRows != null && CONFIG.data.maxRows > 0;
};

// Helper function to apply max rows limit to a query limit
export const applyMaxRowsLimit = (requestedLimit) => {
  const maxRows = getMaxRows();
  
  if (!isMaxRowsEnabled()) {
    return requestedLimit;
  }
  
  if (requestedLimit == null) {
    return maxRows;
  }
  
  return Math.min(requestedLimit, maxRows);
};

// Helper function to get effective limit for queries
export const getEffectiveLimit = (requestedLimit) => {
  const appliedLimit = applyMaxRowsLimit(requestedLimit);
  return appliedLimit || CONFIG.data.defaultLimit;
};

// Helper function to check if console logging is enabled
export const isConsoleLoggingEnabled = () => {
  return CONFIG.development.enableConsoleLogging;
};

// Helper function to check if cache logging is enabled
export const isCacheLoggingEnabled = () => {
  return CONFIG.development.enableCacheLogging;
};

// Helper function to check if performance logging is enabled
export const isPerformanceLoggingEnabled = () => {
  return CONFIG.development.enablePerformanceLogging;
};

// Helper function to conditionally log based on environment
export const devLog = (...args) => {
  if (isConsoleLoggingEnabled()) {
    console.log(...args);
  }
};

// Helper function to conditionally log cache operations
export const cacheLog = (...args) => {
  if (isCacheLoggingEnabled()) {
    console.log('📋', ...args);
  }
};

// Helper function to conditionally log performance metrics
export const perfLog = (...args) => {
  if (isPerformanceLoggingEnabled()) {
    console.log('⚡', ...args);
  }
};

// Helper to get blob file manager URL
export const getBlobFileManagerUrl = () => CONFIG.api.blobFileManagerUrl;

export default CONFIG;